// Web AuthService. The platform-neutral methods (sign in/up/out, password reset,
// current user, app settings, etc.) live in @adrodd/core's CoreAuthService so the
// React Native app can reuse them. This subclass adds the WEB-only OAuth redirect
// flow (window.location + Vite runtimeEnv). React Native will implement native
// OAuth separately (Phase 4). Existing imports of `@/services/AuthService` —
// `authService` and the auth types — continue to work unchanged.
import { CoreAuthService } from '@adrodd/core';
import type { User } from '@adrodd/core';
import apiClient from '@/lib/apiClient';
import { readFirstTouch } from '@/lib/firstTouch';
import runtimeEnv from '@/lib/runtimeEnv';
import { generateTraceId } from '@adrodd/core';
import { logAudit } from '@adrodd/core';

type OAuthProvider = 'apple' | 'google' | 'microsoft' | 'discord';
type DiscordOAuthPurpose = 'sign_in' | 'link';

class AuthService extends CoreAuthService {
  // OAuth sign in (Apple, Google, Microsoft) — web redirect flow.
  async signInWithOAuth(provider: OAuthProvider, linkToken?: string): Promise<void> {
    if (provider === 'discord') {
      return this.startDiscordOAuth('sign_in');
    }

    // Redirect to Rails OAuth endpoint
    const browserProtocol = typeof window !== 'undefined' ? window.location.protocol.replace(':', '') : 'http';
    const browserHost = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
    const fallbackProtocol = runtimeEnv.get('VITE_API_PROTOCOL', browserProtocol || 'http');
    const fallbackHost = runtimeEnv.get('VITE_API_HOST', browserHost || 'localhost');
    const fallbackPort = runtimeEnv.get('VITE_API_PORT', '3000');
    const fallbackApiUrl = [fallbackProtocol, '://', fallbackHost, ':', fallbackPort].join('');
    const apiUrl = runtimeEnv.getApiOrigin() || fallbackApiUrl;
    const traceId = generateTraceId();
    logAudit('oauth_sign_in_redirect', { provider }, traceId);

    // Apple's browser flow is our own redirect (Api::AppleOauthController), not OmniAuth.
    if (provider === 'apple') {
      window.location.assign(`${apiUrl}/api/auth/apple/start?platform=web`);
      return;
    }

    // A link goes straight to the OmniAuth path (/users/auth/* is not needed there). A sign-in
    // carries the first touch, which OmniAuth hands back on the callback: the only place a
    // Google/Microsoft signup can learn it.
    const touch = readFirstTouch();
    const redirectUrl = linkToken
      ? `${apiUrl}/api/auth/google_oauth2?link_token=${encodeURIComponent(linkToken)}`
      : `${apiUrl}/users/auth/${provider}${touch ? `?${touch}` : ''}`;
    window.location.assign(redirectUrl);
  }

  /** Attach a Google account to the signed-in user instead of signing in with it. */
  async startGoogleLink(): Promise<void> {
    const response = await apiClient.post<{ link_token: string }>('/api/auth/google/link_token');
    return this.signInWithOAuth('google', response.data.link_token);
  }

  async startDiscordOAuth(purpose: DiscordOAuthPurpose): Promise<void> {
    const response = await apiClient.post<{ authorization_url: string }>('/api/auth/discord/start', {
      platform: 'web',
      purpose,
    });
    window.location.assign(response.data.authorization_url);
  }

  async exchangeDiscordCode(exchangeCode: string): Promise<{ user: User; purpose: 'sign_in' | 'link' }> {
    const response = await apiClient.post<{ user: User; purpose: 'sign_in' | 'link' }>('/api/auth/discord/exchange', {
      exchange_code: exchangeCode,
    });
    return response.data;
  }
}

export const authService = new AuthService();

// Re-export the auth types from core so existing `@/services/AuthService` type
// imports (ProviderChip, UserService, test-utils, etc.) keep resolving.
export type { TierInfo, LinkedIdentity, User, AuthResponse } from '@adrodd/core';
